import {
    type IFontBackground,
    type IFontGlow,
    type IFontShadow,
    type IFontStroke,
    type ILayerProps,
    type ITextLayerProps,
} from '@/version-types';

export class EffectCreator {
    private constructor() {
        throw new Error('EffectCreator is a static class');
    }

    private static readonly baseFontSize = 36;

    public static getRandomColor() {
        return `#${Math.floor(Math.random() * 0xffffff)
            .toString(16)
            .padStart(6, '0')}ff`;
    }

    public static createStroke(fontSize: number, outerColor?: string): IFontStroke {
        const ratio = fontSize / this.baseFontSize;
        const width = Math.round(2 * ratio);
        const color = outerColor || this.getRandomColor();

        return {
            width,
            color,
            direction: 0,
            hidden: false,
            editable: true,
        };
    }

    public static createGlow(fontSize: number, outerColor?: string): IFontGlow {
        const ratio = fontSize / this.baseFontSize;
        const width = Math.round(5 * ratio);
        const color = outerColor || '#fb35ddff';

        return {
            width,
            blur: 5,
            color,
            hidden: false,
            editable: true,
        };
    }

    public static createShadow(fontSize: number, outerColor?: string): IFontShadow {
        const color = outerColor || '#000000ff';
        const ratio = fontSize / this.baseFontSize;
        const y = Math.round(2.8 * ratio);

        return {
            blur: 2,
            color,
            x: 0,
            y,
            hidden: false,
            editable: true,
        };
    }

    public static createBackground(fontSize: number, outerColor?: string): IFontBackground {
        const color = outerColor || '#fcca00ff';
        const roundWeight = Math.ceil(fontSize / 2);

        return {
            color,
            roundWeight,
            hidden: false,
            editable: true,
        };
    }
}

export const FeatureChecker = {
    usingGradient: (layer: ILayerProps) => {
        const { textGradient } = layer.data as ITextLayerProps;
        return !!textGradient && textGradient.colors.length > 0;
    },

    usingTexture: (layer: ILayerProps) => {
        const { texture } = layer.data as ITextLayerProps;
        return !!texture && !!texture.url;
    },

    usingStroke: (layer: ILayerProps, options = { includeHidden: false }) => {
        const { textStroke } = layer.data as ITextLayerProps;
        const hasStroke = !!textStroke && textStroke.length > 0;

        if (options.includeHidden) {
            return hasStroke;
        }

        return hasStroke && textStroke.filter((stroke) => !stroke.hidden).length > 0;
    },

    usingShadow: (layer: ILayerProps, options = { includeHidden: false }) => {
        const { textShadow } = layer.data as ITextLayerProps;
        const hasShadow = !!textShadow && textShadow.length > 0;

        if (options.includeHidden) {
            return hasShadow;
        }

        return hasShadow && textShadow.filter((shadow) => !shadow.hidden).length > 0;
    },

    usingGlow: (layer: ILayerProps, options = { includeHidden: false }) => {
        const { textGlow } = layer.data as ITextLayerProps;
        const hasGlow = !!textGlow && textGlow.length > 0;

        if (options.includeHidden) {
            return hasGlow;
        }

        return hasGlow && textGlow.filter((glow) => !glow.hidden).length > 0;
    },

    usingTextMark: (layer: ILayerProps) => {
        const { textMark, textContents = [] } = layer.data as ITextLayerProps;

        if (textMark) return true;
        if (textContents.length > 0) {
            return textContents.some((item) => !!item.textMark);
        }

        return false;
    },

    usingBackground: (layer: ILayerProps) => {
        const { textBackground } = layer.data as ITextLayerProps;
        return textBackground && textBackground.length > 0 && textBackground[0].color;
    },

    hasNoEffects: (layer: ILayerProps) => {
        const { textShadow = [], textStroke = [], textGlow = [], textBackground = [] } = layer.data as ITextLayerProps;

        const effects = [textShadow, textStroke, textGlow, textBackground];

        return !effects.some((effect) => effect.length > 0);
    },
};

/**
 * 获取计算后的描边列表
 * 由于描边是通过叠加实现的，越外层的描边越宽，因此需要重新计算每个描边的宽度
 * @param strokes - 描边列表
 * @returns 计算后的描边列表
 */
export const getComputedStrokes = (strokes: IFontStroke[]) => {
    if (strokes.length === 0) return [];

    let width = 0;
    return strokes.map((stroke, idx) => {
        width += stroke.width;
        const next = {
            idx,
            width,
            color: stroke.color,
            hidden: stroke.hidden || false,
        };
        return next;
    });
};
